/**
 * Site header behaviour — two custom elements, no scroll listener.
 *
 * <site-header>  publishes --header-height, decides the scrolled state, and
 *                opens the mobile drawer.
 * <mega-menu>    mirrors a native popover's open state onto its trigger's
 *                aria-expanded and tells the header to dim the page.
 *
 * WHY NO SCROLL LISTENER
 * ----------------------
 * The header this replaces recomputed classes inside a `window.addEventListener
 * ('scroll')` on every frame. The trigger here is an IntersectionObserver, so
 * the browser tells us when the boundary is crossed and nothing runs in
 * between: a 1px sentinel at the document origin leaves the viewport the moment
 * the page moves, and that is the whole of `is-scrolled`.
 *
 * BOTH MODES USE THAT SENTINEL, which they did not always. Team mode used to
 * watch the team's colour band instead — the element carrying
 * [data-header-backdrop], with a root margin of the utility bar — so the header
 * only went opaque "once past that team's coloured block", the designer's own
 * wording. What that missed is that the band SCROLLS UNDER the transparent team
 * row on its way out: for the whole of that distance the band's own heading sat
 * on top of the nav pill with nothing between them, and the white bar arrived
 * only after the collision had finished. So the transparent treatment is now
 * what you get at the very top of a backdrop page and nowhere else.
 */

const BREAKPOINT = 750;

if (!customElements.get('site-header')) {
  class SiteHeader extends HTMLElement {
    observer = null;
    resizeObserver = null;
    sentinel = null;

    connectedCallback() {
      /* `.site-header-group` is the wrapper in layout/theme.liquid, NOT a
         Shopify-emitted element. Shopify emits a section group's name only as a
         CLASS on each member section — never as one wrapper with an
         `id="shopify-section-group-…"` — so `closest('[id^="shopify-section-group-"]')`
         matches nothing and silently falls back to `this.parentElement`, the
         header section alone. That publishes `--header-height: 80px` against a
         real 110px of header, and everything offsetting against it is short by
         the ticker. Regressed once already; do not "simplify" it back. */
      this.group = this.closest('.site-header-group') || this.parentElement;

      this.publishHeight();
      this.watchHeight();
      this.observeScroll();

      /* Crossing the layout breakpoint changes BOTH numbers this element
         publishes: the header's height (48px controls become 36px) and the
         scroll observer's root margin (110px of bar becomes 76px). A
         `matchMedia` change fires once per crossing rather than once per resize
         frame, so this is not a resize listener in disguise. */
      this.breakpoint = window.matchMedia(`(min-width: ${BREAKPOINT}px)`);
      this.breakpoint.addEventListener('change', this.onBreakpointChange);

      this.addEventListener('click', this.onClick);
      /* Capture, for the same reason `<mega-menu>` uses it: the quick-add
         opener stops propagation, so a bubbling listener never sees it. */
      this.addEventListener('click', this.onProductAction, true);
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.sentinel?.remove();
      this.breakpoint?.removeEventListener('change', this.onBreakpointChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('click', this.onProductAction, true);
      document.removeEventListener('keydown', this.onKeydown);
    }

    onBreakpointChange = () => {
      this.publishHeight();
      this.observeScroll();
    };

    get utility() {
      return this.querySelector('.site-header__utility');
    }

    get drawer() {
      const trigger = this.querySelector('[data-nav-drawer-open]');
      return trigger ? document.getElementById(trigger.dataset.navDrawerOpen) : null;
    }

    /* `--header-height` is read by component-search-overlay.css (the panel's
       `top` and its max-height), component-filters-sidebar.liquid and the mega
       menu panel's own `top`, so it has to stay accurate through every state
       change, not just on load. The overlay depends on it across all four
       header heights — 110/174/190 desktop, 76/132/152 mobile. */
    publishHeight() {
      const height = this.group?.offsetHeight || this.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);

      /* `--header-height` is the WHOLE header, team row included (174 desktop,
         132 mobile in team mode). The mobile drawer wants to sit below the MAIN
         header only — the ticker and the utility row — because it replaces the
         team navigation and so covers the team row rather than clearing it.
         That is 76 on mobile and 110 on desktop, and `stableOffset` already
         measures exactly it for the scroll observer. */
      document.documentElement.style.setProperty('--header-bar-height', `${this.stableOffset}px`);
    }

    watchHeight() {
      if (!('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(() => {
        this.publishHeight();
      });
      this.resizeObserver.observe(this.group || this);
    }

    /* The offset that stays put across both team-row heights: the ticker plus
       the utility row, measured as a distance rather than read off either
       element's own box so it holds whether or not the group is stuck. */
    get stableOffset() {
      if (!this.group) return 0;

      const groupTop = this.group.getBoundingClientRect().top;
      const utility = this.utility;

      /* Pages that hide the utility row still have a ticker above the team
         row, and this element's own top edge IS the ticker's bottom. Returning
         0 here instead floats the mobile nav drawer over the ticker, since
         component-nav-drawer.css offsets it by exactly this value. */
      if (!utility) return Math.round(this.getBoundingClientRect().top - groupTop);

      return Math.round(utility.getBoundingClientRect().bottom - groupTop);
    }

    observeScroll() {
      this.observer?.disconnect();

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.classList.toggle('is-scrolled', !entry.isIntersecting);
          }
          this.publishHeight();
        },
        { threshold: 0 },
      );

      this.observer.observe(this.ensureSentinel());
    }

    ensureSentinel() {
      if (this.sentinel) return this.sentinel;

      this.sentinel = document.createElement('div');
      this.sentinel.className = 'site-header__sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      document.body.prepend(this.sentinel);
      return this.sentinel;
    }

    /* The mobile drawer is a <dialog> opened with showModal(), so it is in the
       TOP LAYER and paints over everything regardless of z-index — including
       the quick-add dialog, which is a plain `[role="dialog"]` div. A product
       opened from inside the drawer therefore loaded correctly but rendered
       behind the drawer it was opened from.

       Closing the drawer on the card's own controls fixes that, and matches
       what `<mega-menu>` does for the desktop panel. */
    onProductAction = (event) => {
      if (!event.target.closest('modal-opener, .mega-menu-card__action, .product-tile__action')) return;
      this.closeDrawer();
    };

    onClick = (event) => {
      const opener = event.target.closest('[data-nav-drawer-open]');
      if (opener) {
        this.openDrawer();
        return;
      }

      const closer = event.target.closest('[data-nav-drawer-close]');
      if (closer) this.closeDrawer();
    };

    /* Escape and the focus trap belong to <dialog>; this only covers the mega
       menu panels, where the popover already handles Escape itself but the
       header still needs to drop the scrim if one closes without a toggle
       event reaching us. */
    onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (!this.classList.contains('is-menu-open')) return;
      if (!this.querySelector('.mega-menu__panel:popover-open')) {
        this.classList.remove('is-menu-open');
      }
    };

    openDrawer() {
      const drawer = this.drawer;
      if (!drawer) return;

      /* showModal() gives the focus trap, Escape, the inert background and the
         backdrop. The page behind is held by the `scroll-lock` attribute in
         critical.css, so nothing here has to remember to unlock it. */
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');

      /* The dialog's own `::backdrop` is transparent, because it would dim the
         header too. The header's scrim does the dimming instead — it sits below
         the bars, so they stay at full brightness, which is what the frame
         shows. Same scrim the desktop panels use. */
      this.setMenuOpen(true);
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer) return;

      if (typeof drawer.close === 'function') drawer.close();
      else drawer.removeAttribute('open');

      this.setMenuOpen(false);
    }

    setMenuOpen(isOpen) {
      /* No breakpoint guard any more. It used to skip the scrim below the
         breakpoint on the grounds that the panels are not rendered there and the
         drawer dimmed the page itself — which it did, through its own
         `::backdrop`. That backdrop is transparent now, because it covered the
         header as well and the frame keeps the header at full brightness, so
         below the breakpoint this scrim is the ONLY thing dimming the page.
         Restoring the guard leaves the mobile drawer floating over an undimmed
         page. */
      this.classList.toggle('is-menu-open', isOpen);
    }
  }

  customElements.define('site-header', SiteHeader);
}

if (!customElements.get('mega-menu')) {
  class MegaMenu extends HTMLElement {
    /* Long enough to cross the 22px gap between the pill and the panel without
       the panel closing under the cursor, short enough not to feel sticky. */
    static CLOSE_DELAY = 180;

    closeTimer = null;

    connectedCallback() {
      this.panel?.addEventListener('toggle', this.onToggle);

      /* Hover opening is desktop-only, and deliberately additive: `popovertarget`
         still opens the panel on click, so the keyboard path and the no-JS path
         are untouched. On a touch screen there is no hover to speak of and the
         first tap would both open and dismiss, so the pointer query gates it —
         below 750px the panel is display:none anyway and the drawer takes over. */
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      /* CAPTURE, not bubble: `component-modal-opener.js` calls
         stopPropagation() on its button's click, so a bubbling listener here
         never sees the quick-add opener at all. Capture runs on the way down,
         before the target's own handler, so it is unaffected. */
      this.addEventListener('click', this.onPanelClick, true);
      this.addEventListener('click', this.onExpandClick);
    }

    disconnectedCallback() {
      this.panel?.removeEventListener('toggle', this.onToggle);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('click', this.onPanelClick, true);
      this.removeEventListener('click', this.onExpandClick);
      clearTimeout(this.closeTimer);
    }

    /* The panel is a native popover, so it sits in the TOP LAYER and paints over
       everything regardless of z-index. The quick-add dialog is a plain
       `[role="dialog"]` div, so a product opened from inside the panel rendered
       BEHIND the menu it was opened from.

       Closing the panel on the card's own controls fixes that and is the right
       behaviour anyway — the shopper has moved on to a product. It covers all
       three add-to-cart shapes: the quick-add opener, the single-variant submit,
       and the product tiles on the Sale panel. */
    onPanelClick = (event) => {
      if (!event.target.closest('modal-opener, .mega-menu-card__action, .product-tile__action')) return;

      clearTimeout(this.closeTimer);
      if (this.panel?.matches(':popover-open')) {
        this.panel.hidePopover();
      }
    };

    onPointerEnter = () => {
      if (!this.hoverQuery?.matches) return;

      clearTimeout(this.closeTimer);

      /* showPopover() throws if it is already open, and an auto popover closes
         whichever sibling was open for us, so moving along the nav swaps panels
         without any bookkeeping here. */
      const panel = this.panel;
      if (panel && !panel.matches(':popover-open')) {
        panel.showPopover();
      }
    };

    onPointerLeave = () => {
      if (!this.hoverQuery?.matches) return;

      /* The panel sits 22px below the pill with nothing in between, so the
         pointer leaves this element on the way to it. Closing on a timer that
         re-entering cancels is what stops that gap from flickering the panel
         shut. */
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        /* hidePopover() throws on a popover that is not showing, and by the time
           the timer fires an auto popover may already have been dismissed by a
           click elsewhere or by another panel opening. */
        const panel = this.panel;
        if (panel?.matches(':popover-open')) {
          panel.hidePopover();
        }
      }, MegaMenu.CLOSE_DELAY);
    };

    /**
     * "All brands" / "All types" open the column's overflow box instead of
     * navigating.
     *
     * The link keeps its href, so with no JS — or if this throws — it still
     * goes to the all-of-them collection. preventDefault only happens once the
     * box has actually been found, never optimistically.
     *
     * NOT in the capture phase, unlike onPanelClick: that one runs early
     * because it has to see clicks the card markup would otherwise swallow.
     * This is an ordinary link click and wants the normal order, so a modifier
     * click is still a modifier click.
     */
    onExpandClick = (event) => {
      const toggle = event.target.closest('[data-mega-expand]');
      if (!toggle) return;

      /* Let the browser do what the shopper asked: a new tab, a download, a
         saved link. Only a plain left click is ours to intercept. */
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button !== 0) return;

      const box = this.querySelector(`#${CSS.escape(toggle.dataset.megaExpand)}`);
      if (!box) return;

      event.preventDefault();

      const opening = box.hidden;

      /* One box at a time. Two open at once would push the featured products
         off the bottom of a panel that is already the height of the viewport. */
      for (const other of this.querySelectorAll('.mega-menu__expanded')) {
        other.hidden = true;
      }
      for (const other of this.querySelectorAll('[data-mega-expand]')) {
        other.setAttribute('aria-expanded', 'false');
      }

      box.hidden = !opening;
      toggle.setAttribute('aria-expanded', String(opening));
    };

    get panel() {
      return this.querySelector('[popover]');
    }

    get trigger() {
      return this.querySelector('[popovertarget]');
    }

    /* The popover owns the open/close, Escape, click-outside and returning
       focus to the trigger. All that is left is telling assistive technology
       what the button did, and asking the header for the scrim — an auto
       popover closes any other that was open, and each fires its own toggle,
       so the last event wins and the scrim state stays correct. */
    /* Reset on close, so reopening the panel does not show a box the shopper
       opened minutes ago on a different visit to the same menu. */
    closeExpanded = () => {
      for (const box of this.querySelectorAll('.mega-menu__expanded')) {
        box.hidden = true;
      }
      for (const toggle of this.querySelectorAll('[data-mega-expand]')) {
        toggle.setAttribute('aria-expanded', 'false');
      }
    };

    onToggle = (event) => {
      const isOpen = event.newState === 'open';
      this.trigger?.setAttribute('aria-expanded', String(isOpen));
      this.closest('site-header')?.setMenuOpen(isOpen);

      if (!isOpen) this.closeExpanded();
    };
  }

  customElements.define('mega-menu', MegaMenu);
}
